import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ArrowUp, Loader2, Zap } from "lucide-react";
import { Overline } from "./motion";
import { useTranslate } from "../context/TranslateContext";
import { askCinemaAI, getAiFaq } from "../lib/api";

const SUGGESTIONS = [
  "I liked Drishyam. What else should I watch?",
  "Find Malayalam thrillers from the last 5 years",
  "Best Indian films about partition",
  "Suggest a family movie under 2 hours",
];

let SESSION = Math.random().toString(36).slice(2);

export const CinemaAISection = ({ onOpen }) => {
  const { t } = useTranslate();
  const [value, setValue] = useState("");
  const listenText = "Ask Cinema AI anything. Tell it what you're in the mood for and it will recommend Indian films and music across every language, drawing on the platform's structured knowledge.";

  return (
    <section id="ai" className="relative py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="relative overflow-hidden rounded-3xl border border-gold/30 bg-surface p-8 md:p-16">
          {/* tracing beam glow */}
          <motion.div aria-hidden className="pointer-events-none absolute -inset-40 opacity-40"
            style={{ background: "conic-gradient(from 0deg, transparent 0deg, rgba(212,175,55,0.35) 40deg, transparent 120deg)" }}
            animate={{ rotate: 360 }} transition={{ duration: 14, repeat: Infinity, ease: "linear" }} />
          <div className="pointer-events-none absolute inset-[1px] rounded-3xl bg-surface" />

          <div className="relative z-10 grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5">
                <Sparkles className="h-4 w-4 text-gold" />
                <Overline className="!tracking-[0.2em]">Cinema Concierge AI</Overline>
              </div>
              <h2 className="mt-6 font-display text-4xl font-semibold leading-tight tracking-tight text-cream md:text-6xl">
                {t("What should I ")}<span className="italic text-gradient-gold">{t("watch?")}</span>
              </h2>
              <p className="mt-6 font-body text-lg leading-relaxed text-ash">
                {t("Talk to it like a friend who has seen everything. Mood, language, era, runtime — describe it, and get a curated shortlist in seconds.")}
              </p>
              <div className="mt-8"></div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-edge bg-surface p-2">
                <div className="flex items-center gap-3 rounded-xl bg-surface/60 px-4 py-3">
                  <Sparkles className="h-5 w-5 shrink-0 text-gold" />
                  <input
                    data-testid="ai-inline-input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && value.trim() && onOpen(value)}
                    placeholder={t("Ask about any film, song or artist...")}
                    className="flex-1 bg-transparent font-body text-cream placeholder:text-ash focus:outline-none"
                  />
                  <button data-testid="ai-inline-submit" onClick={() => value.trim() && onOpen(value)}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-gold text-ink transition-transform duration-300 hover:scale-110">
                    <ArrowUp className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 p-3">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="ai-suggestion" onClick={() => onOpen(s)}
                      className="rounded-full border border-edge px-3 py-1.5 text-left font-body text-xs text-ash transition-colors duration-300 hover:border-gold hover:text-gold">
                      {t(s)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const AiModal = ({ open, onClose, seed, context }) => {
  const { t, lang } = useTranslate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [faq, setFaq] = useState([]);
  const scrollRef = useRef(null);
  const seeded = useRef(null);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { reply, cached } = await askCinemaAI(q, SESSION, context || undefined, lang.name || "English");
      setMessages((m) => [...m, { role: "ai", text: reply, cached }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "The concierge is unavailable right now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open && seed && seeded.current !== seed) { seeded.current = seed; send(seed); }
    if (!open) seeded.current = null;
  }, [open, seed]);

  // Surface popular saved questions (reusable knowledge base) in the empty state.
  useEffect(() => {
    if (open) getAiFaq(undefined, 6).then((d) => setFaq(d || [])).catch(() => {});
  }, [open]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages, busy]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/70 p-0 backdrop-blur-sm md:items-center md:p-6"
          onClick={onClose} data-testid="ai-modal">
          <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="flex h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl border border-edge bg-surface md:h-[70vh] md:rounded-3xl">
            <div className="flex items-center justify-between border-b border-edge px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15">
                  <Sparkles className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <div className="font-display text-lg text-cream">{t("Cinema Concierge")}</div>
                  <div className="font-body text-xs text-ash">{t("Your Indian cinema & music guide")}</div>
                </div>
              </div>
              <button onClick={onClose} data-testid="ai-modal-close" className="text-ash hover:text-cream"><X className="h-5 w-5" /></button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-6 py-6" data-testid="ai-messages">
              {messages.length === 0 && !busy && (
                <div className="mt-8" data-testid="ai-empty">
                  <div className="text-center font-body text-ash">{t("Ask me what to watch tonight.")}</div>
                  {faq.length > 0 && (
                    <div className="mt-6">
                      <div className="mb-3 flex items-center justify-center gap-2 font-body text-xs uppercase tracking-[0.2em] text-gold">
                        <Zap className="h-3.5 w-3.5" /> {t("Popular questions")}
                      </div>
                      <div className="flex flex-wrap justify-center gap-2">
                        {faq.map((f) => (
                          <button key={f.id} data-testid="ai-faq-chip" onClick={() => send(f.question)}
                            className="rounded-full border border-edge px-3 py-1.5 text-left font-body text-xs text-ash transition-colors duration-300 hover:border-gold hover:text-gold">
                            {f.question}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 font-body text-[0.95rem] leading-relaxed ${
                    m.role === "user" ? "bg-gold text-ink" : "border border-edge bg-surface text-cream"}`}>
                    {m.text}
                    {m.role === "ai" && m.cached && (
                      <div className="mt-2 flex items-center gap-1 font-body text-[0.7rem] text-gold" data-testid="ai-cached-badge">
                        <Zap className="h-3 w-3" /> {t("Answered instantly from our library")}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-2xl border border-edge bg-surface px-4 py-3 text-gold">
                    <Loader2 className="h-4 w-4 animate-spin" /> <span className="font-body text-sm text-ash">{t("Thinking...")}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-edge p-4">
              <div className="flex items-center gap-3 rounded-full border border-edge bg-surface px-4 py-2">
                <input data-testid="ai-modal-input" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("Type your question...")}
                  className="flex-1 bg-transparent font-body text-cream placeholder:text-ash focus:outline-none" />
                <button data-testid="ai-modal-send" onClick={() => send()} disabled={busy}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-gold text-ink transition-transform duration-300 hover:scale-110 disabled:opacity-50">
                  <ArrowUp className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
